import { useCallback, useState } from "react";
import type { ProblemDetails } from "@/lib/problemDetails";

type ApiResult<TData> = {
  data?: TData;
  error?: ProblemDetails;
};

export interface UseApiActionResult<TArgs extends unknown[], TData> {
  run: (...args: TArgs) => Promise<TData | null>;
  pending: boolean;
  error: ProblemDetails | null;
  data: TData | null;
  reset: () => void;
}

/** Wraps an openapi-fetch mutation with pending/error state and its ProblemDetails payload. */
export function useApiAction<TArgs extends unknown[], TData>(
  fn: (...args: TArgs) => Promise<ApiResult<TData>>,
): UseApiActionResult<TArgs, TData> {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ProblemDetails | null>(null);
  const [data, setData] = useState<TData | null>(null);

  const run = useCallback(
    async (...args: TArgs): Promise<TData | null> => {
      setPending(true);
      setError(null);
      try {
        const result = await fn(...args);
        if (result.error) {
          setError(result.error);
          return null;
        }
        const value = result.data ?? null;
        setData(value);
        return value;
      } catch (err) {
        setError({
          title: "Network error",
          detail: err instanceof Error ? err.message : String(err),
        });
        return null;
      } finally {
        setPending(false);
      }
    },
    [fn],
  );

  const reset = useCallback(() => {
    setError(null);
    setData(null);
  }, []);

  return { run, pending, error, data, reset };
}
